/* ============================================================
   EX2 HOME — behaviour for the ex2-* homepage sections
   File: assets/ex2-home.js   (loaded with `defer`)

   Three jobs, no dependencies, ~3KB:
     1. Reveal-on-scroll for .vx-r (IntersectionObserver, one-shot)
     2. Seamless marquee looping (clones the track once)
     3. Quick-add to cart for single-variant product cards

   Everything degrades: if this file never loads, `vx-js` is still
   set inline by each section so content stays visible, marquees
   still scroll (they just repeat with a seam), and every product
   card is a plain link to the PDP.
   ============================================================ */
(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.add('vx-js');

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Reveal on scroll ---------- */

  function revealAll(nodes) {
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-in');
  }

  function initReveal(scope) {
    var items = (scope || document).querySelectorAll('.vx-r:not(.is-in)');
    if (!items.length) return;

    if (!('IntersectionObserver' in window) || reduceMotion) {
      revealAll(items);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      // Fire slightly before the element reaches the viewport so the
      // motion reads as "already settling" rather than "popping in".
      { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
    );

    for (var i = 0; i < items.length; i++) {
      // Anything already on screen at load reveals immediately —
      // no first-paint animation on the hero.
      var box = items[i].getBoundingClientRect();
      if (box.top < window.innerHeight * 0.92) {
        items[i].classList.add('is-in');
      } else {
        io.observe(items[i]);
      }
    }
  }

  /* ---------- 2. Marquee ---------- */
  /* The CSS animates the track to -50%, which only loops seamlessly
     if the content is present exactly twice. Clone it here. */

  function initMarquee(scope) {
    var tracks = (scope || document).querySelectorAll('.vx-marquee__track:not([data-vx-cloned])');
    for (var i = 0; i < tracks.length; i++) {
      var track = tracks[i];
      track.setAttribute('data-vx-cloned', 'true');
      var original = Array.prototype.slice.call(track.children);
      for (var j = 0; j < original.length; j++) {
        var clone = original[j].cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        // Clones are decorative duplicates — keep them out of the
        // tab order and off the accessibility tree.
        var focusable = clone.querySelectorAll('a, button');
        for (var k = 0; k < focusable.length; k++) focusable[k].setAttribute('tabindex', '-1');
        track.appendChild(clone);
      }
    }
  }

  /* ---------- 3. Quick add ---------- */

  function refreshCartUI() {
    // Dawn exposes the cart bubble as its own section — re-render it
    // rather than reloading the page.
    fetch('/?sections=cart-icon-bubble')
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        if (!data || !data['cart-icon-bubble']) return;
        var target = document.getElementById('cart-icon-bubble');
        if (target) target.innerHTML = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html').querySelector('#cart-icon-bubble').innerHTML;
      })
      .catch(function () {});
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    return false;
  }

  function initQuickAdd(scope) {
    (scope || document).addEventListener('click', function (event) {
      var btn = event.target.closest('[data-vx-add]');
      if (!btn || btn.disabled) return;

      event.preventDefault();
      var id = btn.getAttribute('data-vx-add');
      var fallback = btn.getAttribute('data-vx-url');
      var original = btn.innerHTML;

      btn.disabled = true;
      btn.innerHTML = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          return response.json();
        })
        .then(function () {
          btn.innerHTML = 'Added ✓';
          refreshCartUI();
          document.dispatchEvent(new CustomEvent('ex2:added'));
          if (!openCartDrawer()) {
            setTimeout(function () {
              btn.innerHTML = original;
              btn.disabled = false;
            }, 1600);
          } else {
            btn.innerHTML = original;
            btn.disabled = false;
          }
        })
        .catch(function () {
          // Sold out mid-session, or the cart API is unavailable —
          // send the customer to the product page rather than fail silently.
          if (fallback) {
            window.location.href = fallback;
            return;
          }
          btn.innerHTML = original;
          btn.disabled = false;
        });
    });
  }

  /* ---------- 4. Section texture ----------
     Every ex2 section gets one texture layer. Hero and bundle already
     author their own .ex2__grid span, so only sections missing one get
     it injected — that keeps the markup untouched while making the
     texture universal. Which texture it paints is decided entirely in
     CSS from the theme editor. */

  function decorate(scope) {
    var sections = (scope || document).querySelectorAll('.ex2:not([data-ex2-tex])');

    Array.prototype.forEach.call(sections, function (section) {
      section.setAttribute('data-ex2-tex', '1');

      // Thin strips (the ticker) read as noise with a texture behind them.
      if (section.offsetHeight < 260) return;
      // Respect a texture the section already authored.
      if (section.querySelector(':scope > .ex2__grid')) return;

      var layer = document.createElement('span');
      layer.className = 'ex2__grid';
      layer.setAttribute('aria-hidden', 'true');
      section.insertBefore(layer, section.firstChild);
    });
  }

  /* ---------- Boot ---------- */

  function boot(scope) {
    initReveal(scope);
    initMarquee(scope);
    decorate(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  initQuickAdd(document);

  // Theme editor: re-init whenever a section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
